import React from "react";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { useLeadModal } from "./LeadModalContext";

const CASES = [
  {
    image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80",
    tag: "INSTALLATIONS · HOSPITALITY",
    client: "Boutique Hotel Group · Singapore",
    challenge: "Premium lobby design, but passive guest experience. No follow-up. No repeat bookings.",
    metrics: [
      { v: "680", l: "emails captured · month 1" },
      { v: "34%", l: "engaged guests booked return" },
      { v: "$180K", l: "attributed revenue · 6 mo." },
    ],
    quote: "The space became a lead generation machine. What used to be decoration is now driving loyalty.",
  },
  {
    image: "https://images.unsplash.com/photo-1424847651672-bf20a4b0982b?auto=format&fit=crop&w=1200&q=80",
    tag: "INSTALLATIONS · F&B",
    client: "Michelin-Rated Restaurant · Miami",
    challenge: "Beautiful restaurant, low repeat visit rate (18%), no way to follow up with guests.",
    metrics: [
      { v: "18→42%", l: "repeat-visit rate · 6 mo." },
      { v: "12,000+", l: "guest interactions" },
      { v: "$320K", l: "incremental revenue" },
    ],
    quote: "Dinner became a shareable moment. Guests promote us now.",
  },
  {
    image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80",
    tag: "INSTALLATIONS · RETAIL",
    client: "Fashion Brand Flagship · Tokyo",
    challenge: "High foot traffic but no customer data. Product team flying blind on preferences.",
    metrics: [
      { v: "200+", l: "monthly social posts" },
      { v: "3x", l: "instagram reach growth" },
      { v: "Live", l: "feed into product team" },
    ],
    quote: "The store became a market research tool.",
  },
  {
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=80",
    tag: "ACTIVATION · TECH LAUNCH",
    client: "Tech Brand Product Launch · San Francisco",
    challenge: "Launch event. Need to capture warm leads, generate buzz, measure impact.",
    metrics: [
      { v: "847", l: "qualified leads vs. 120 est." },
      { v: "340", l: "influencers identified" },
      { v: "$2.1M", l: "attributed revenue · 90d" },
    ],
    quote: "We didn't just capture photos. We captured relationships and revenue.",
  },
];

export default function CaseStudies() {
  const { openModal } = useLeadModal();
  return (
    <section
      id="case-studies"
      data-testid="case-studies"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="CASE STUDIES · IN PRODUCTION"
          title="Brands turning guest moments into measurable revenue."
        />

        <div className="mt-16 grid md:grid-cols-2 gap-5">
          {CASES.map((c, i) => (
            <article
              key={i}
              data-testid={`case-study-${i}`}
              className="group rounded-2xl overflow-hidden border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-all"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={c.image}
                  alt={c.client}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stori-bg via-stori-bg/40 to-transparent" />
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-stori-bg/80 backdrop-blur-md border border-stori-border font-mono text-[10px] uppercase tracking-[0.18em] text-stori-cyan">
                  {c.tag}
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="font-heading text-xl text-white font-semibold">{c.client}</div>
                </div>
              </div>

              <div className="p-7">
                <p className="text-stori-muted leading-relaxed text-sm">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stori-cyan mr-2">
                    Challenge
                  </span>
                  {c.challenge}
                </p>

                <div className="mt-6 grid grid-cols-3 gap-2 border-y border-stori-border py-5">
                  {c.metrics.map((m, j) => (
                    <div key={j}>
                      <div className="font-heading text-xl lg:text-2xl text-white font-semibold">
                        {m.v}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-stori-muted leading-tight mt-1">
                        {m.l}
                      </div>
                    </div>
                  ))}
                </div>

                <blockquote className="mt-5 text-white text-base leading-snug">
                  &ldquo;{c.quote}&rdquo;
                </blockquote>

                <button
                  type="button"
                  onClick={() => openModal("demo_request")}
                  className="mt-5 inline-flex items-center gap-2 text-stori-cyan text-sm hover:underline underline-offset-4"
                  data-testid={`case-study-cta-${i}`}
                >
                  Read full case study
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
